import { darkBackgroundBlur, hideScrollBar } from '@src/components/utilities';
import { VerticallyScrollable } from '@src/deprecated/VerticallyScrollable';
import { QUERIES } from '@src/queries';
import { themeStyle } from '@src/theming';
import styled, { css, keyframes, RuleSet } from 'styled-components';

import { MODAL_ANIMATION_DURATION_MS } from './config';
import { ROUND_CLOSE_BUTTON_SIZE } from './RoundCloseButton/styles';
import { ModalState, ModalTheme } from './types';

const scaleKeyframes = keyframes`
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
`;

const scaleAnimationRule = css`
  ${scaleKeyframes};
`;

const opacityKeyframesLight = keyframes`
  from {
    background: rgba(0, 0, 0, 0);
  }
  to {
    background: rgba(0, 0, 0, 0.6);
  }
`;

const opacityAnimationRuleLight = css`
  ${opacityKeyframesLight};
`;

const opacityKeyframesDark = keyframes`
  from {
    background: rgba(0, 0, 0, 0);
  }
  to {
    background: rgba(0, 0, 0, 0.4);
  }
`;

const opacityAnimationRuleDark = css`
  ${opacityKeyframesDark};
`;

const blurAnimation = keyframes`
  from {
    backdrop-filter: blur(0px);
  }
  to {
    backdrop-filter: blur(4px);
  }
`;

const blurAnimationRule = css`
  ${blurAnimation};
`;

export const ExternalCloseButton = styled.div<{ isYoutubeVideo?: boolean }>`
  position: absolute;
  top: -${ROUND_CLOSE_BUTTON_SIZE + 12}px;
  right: ${ROUND_CLOSE_BUTTON_SIZE + 24}px;
  pointer-events: auto;

  ${QUERIES.tabletAndUp} {
    top: -${ROUND_CLOSE_BUTTON_SIZE + 12}px;
    right: ${ROUND_CLOSE_BUTTON_SIZE}px;
  }

  ${QUERIES.laptopAndUp} {
    right: ${({ isYoutubeVideo }) => (isYoutubeVideo ? '20px' : '-12px')};
    top: ${({ isYoutubeVideo }) => (isYoutubeVideo ? '-18px' : '0px')};
    z-index: ${({ isYoutubeVideo }) => (isYoutubeVideo ? '99' : 'unset')};
  }
`;

const getAnimationName = (
  animation: RuleSet,
  blurBackground?: boolean
): ReturnType<typeof css> | '' => {
  if (!blurBackground) {
    return css`
      animation-name: ${animation};
    `;
  }
  return css`
    animation-name: ${animation}, ${blurAnimationRule};
  `;
};

export const Background = styled.div<{
  disableClose?: boolean;
  externalCloseButton?: boolean;
  blurBackground?: boolean;
  state: ModalState;
  theme: Partial<ModalTheme>;
}>`
  width: 100vw;
  height: 100svh;
  position: fixed;
  pointer-events: auto;
  cursor: ${({ disableClose, externalCloseButton }) =>
    disableClose || externalCloseButton ? 'default' : 'pointer'};

  animation-duration: ${MODAL_ANIMATION_DURATION_MS}ms;
  animation-timing-function: ease-in-out;

  ${({ blurBackground }) =>
    getAnimationName(opacityAnimationRuleLight, blurBackground)};
  background-color: ${({ state }) =>
    state.closing || !state.isOpen ? 'rgba(0, 0, 0, 0)' : 'rgba(0, 0, 0, 0.6)'};

  html[data-theme='dark'] &&,
  .dark && {
    ${({ blurBackground }) =>
      getAnimationName(opacityAnimationRuleDark, blurBackground)};
    background-color: ${({ state }) =>
      state.closing || !state.isOpen
        ? 'rgba(0, 0, 0, 0)'
        : 'rgba(0, 0, 0, 0.4)'};
  }

  transition: background-color ease-in-out 0.25s;
  z-index: ${({ theme }) => theme.zIndex};
  backdrop-filter: ${({ blurBackground }) =>
    blurBackground ? 'blur(4px)' : 'unset'};
  -webkit-backdrop-filter: ${({ blurBackground }) =>
    blurBackground ? 'blur(4px)' : 'unset'};
`;

const noScrollBar = css`
  overflow-y: scroll;
  ${hideScrollBar}
`;

const diableBorderRadius = css`
  border-radius: 0;
`;

// Uses dynamic viewport heights to ensure the modal is always visible on the screen
const safeAreaHeight = (height: string) =>
  height === '100vh'
    ? css`
        height: 100vh;
        height: 100svh;
      `
    : css`
        height: ${height};
      `;

interface ModalWrapperProps {
  width: string;
  height: string;
  noOverflowX?: boolean;
  noShadow?: boolean;
  noContentBackground?: boolean;
}

const modalWrapperCommonStyle = css<ModalWrapperProps>`
  width: ${({ width }) => width};
  ${({ height }) => safeAreaHeight(height)}
  max-width: 100%;
  max-height: 100%;
  overflow-x: ${({ noOverflowX }) => (noOverflowX ? 'hidden' : 'visible')};
  pointer-events: auto;

  background: ${({ noContentBackground }) =>
    noContentBackground ? themeStyle.transparent : themeStyle.varBackgroundAlt};
  ${themeStyle.varBackgroundAlt};
  border-radius: ${({ noContentBackground }) =>
    noContentBackground ? 0 : themeStyle.radiusLarge};
  box-shadow: ${({ noShadow, noContentBackground }) =>
    noShadow || noContentBackground ? 'none' : themeStyle.varShadowHeavy};

  ${darkBackgroundBlur}
`;

export const ModalWrapperWithScrollbar = styled(
  VerticallyScrollable
)<ModalWrapperProps>`
  ${modalWrapperCommonStyle}
`;

export const ModalWrapper = styled.div<
  ModalWrapperProps & {
    noOverflow?: boolean;
    hideScrollbar?: boolean;
    noBorderRadius?: boolean;
    noContentBackground?: boolean;
  }
>`
  ${modalWrapperCommonStyle};
  overflow-y: ${({ noOverflow }) => (noOverflow ? 'hidden' : 'auto')};
  ${({ hideScrollbar }) => hideScrollbar && noScrollBar}
  ${({ noBorderRadius }) => noBorderRadius && diableBorderRadius}
`;

export const ContentWrapper = styled.div<{
  height: string;
  noContentBackground?: boolean;
}>`
  ${({ height }) => safeAreaHeight(height)}
  display: flex;
  ${({ noContentBackground }) =>
    !noContentBackground
      ? css`
          align-items: safe center;
          justify-content: safe center;
        `
      : ''}
`;

export const ForegroundWrapper = styled.div<{ theme: Partial<ModalTheme> }>`
  position: absolute;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.padding};
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: ${({ theme }) => theme.zIndex};
`;

export const Foreground = styled.div<{
  state: ModalState;
  stretchContent: boolean;
  left?: string;
  top?: string;
  position?: string;
  bottom?: string;
  right?: string;
}>`
  max-width: 100%;
  max-height: 100%;
  height: ${({ stretchContent }) => (stretchContent ? '100%' : undefined)};
  display: flex;
  ${({ left }) => left && `left: ${left};`}
  ${({ right }) => right && `right: ${right};`}
  ${({ top }) => top && `top: ${top};`}
  ${({ bottom }) => bottom && `bottom: ${bottom};`}
  ${({ position }) => position && `position: ${position};`}
  animation-name: ${scaleAnimationRule};
  animation-duration: ${MODAL_ANIMATION_DURATION_MS}ms;
  animation-timing-function: ease-in-out;

  transform: ${({ state }) =>
    ((state.closing || !state.isOpen) && 'scale(0)') || 'scale(1)'};
  transition: transform ease-in-out 0.25s;
  will-change: transform;
`;
